import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  Button,
  Form,
  Select,
  Tag,
  Alert,
  Input,
  Collapse,
  Modal,
  notification,
} from "antd";
import Breadcrumbs from "../../components/Breadcrumbs";
import { FreireContext } from "../../utils/freireContext";
import api from "../../services/api";
import { LocaleText } from "./locales";
import * as texts from "./locales";
import { API_DOCS, ApiEndpoint } from "./apiSchemas";

const { Option } = Select;
const { Panel } = Collapse;

interface Company {
  _id: string;
  companyName: string;
  ocpiPartyId: string;
}

interface OcpiPartyTokenPreview {
  token?: string | null;
}

interface OcpiPartyTokens {
  first?: OcpiPartyTokenPreview | null;
  generated?: OcpiPartyTokenPreview | null;
  access?: OcpiPartyTokenPreview | null;
}

interface OcpiPartyDetails {
  tokens?: OcpiPartyTokens | null;
}

const METHOD_COLORS: Record<string, string> = {
  GET: "green",
  POST: "blue",
  PUT: "orange",
  DELETE: "red",
  PATCH: "cyan",
};

const PARAM_IN_COLORS: Record<string, string> = {
  header: "purple",
  path: "geekblue",
  query: "cyan",
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const thStyle: React.CSSProperties = {
  padding: "6px 12px",
  textAlign: "left",
  borderBottom: "1px solid #e8e8e8",
  fontWeight: 600,
  whiteSpace: "nowrap",
};

const tdStyle: React.CSSProperties = {
  padding: "6px 12px",
  borderBottom: "1px solid #f0f0f0",
  verticalAlign: "top",
};

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  marginTop: 8,
  fontSize: 13,
};

const metaRowStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 24,
  marginBottom: 12,
  fontSize: 13,
  color: "rgba(0,0,0,0.65)",
};

const valueTagStyle: React.CSSProperties = {
  margin: "0 4px 4px 0",
  fontFamily: "monospace",
  fontSize: 11,
};

const inlineCodeStyle: React.CSSProperties = {
  background: "#f5f5f5",
  border: "1px solid #e8e8e8",
  borderRadius: 3,
  padding: "1px 6px",
  fontSize: 12,
};

const codeBlockStyle: React.CSSProperties = {
  background: "#282c34",
  color: "#abb2bf",
  padding: 16,
  borderRadius: 4,
  marginTop: 8,
  fontSize: 13,
  overflowX: "auto",
  whiteSpace: "pre",
};

// ─── EndpointCard ─────────────────────────────────────────────────────────────

interface EndpointCardProps {
  endpoint: ApiEndpoint;
  freire: (text: LocaleText) => string;
}

// A column maps a row item to a header label and a rendered cell. Driving every
// section table through one component keeps the markup in a single place and
// keeps the rendered JSX out of array literals (each cell is produced by a
// render function, and the <td>/<tr>/<th> keys are owned here).
interface Column<T> {
  key: string;
  title: string;
  render: (item: T) => React.ReactNode;
}

function SimpleTable<T>({
  columns,
  items,
}: Readonly<{ columns: Column<T>[]; items: T[] }>) {
  return (
    <table style={tableStyle}>
      <thead>
        <tr style={{ background: "#fafafa" }}>
          {columns.map((column) => (
            <th key={column.key} style={thStyle}>
              {column.title}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {items.map((item, rowIndex) => (
          <tr key={rowKey(item, rowIndex)}>
            {columns.map((column) => (
              <td key={column.key} style={tdStyle}>
                {column.render(item)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Stable row key derived from the item when possible, falling back to the index.
const rowKey = (item: unknown, index: number): string => {
  if (item && typeof item === "object") {
    const candidate = item as { path?: string; name?: string; code?: string };
    const id = candidate.path ?? candidate.name ?? candidate.code;
    if (id) return id;
  }
  return `row-${index}`;
};

const requiredTag = (required: boolean, freire: (text: LocaleText) => string) =>
  required ? (
    <Tag color="red">{freire(texts.REQUIRED_YES)}</Tag>
  ) : (
    <Tag>{freire(texts.REQUIRED_NO)}</Tag>
  );

const valuesCell = (values?: string[]) =>
  values
    ? values.map((v) => (
        <Tag key={v} style={valueTagStyle}>
          {v}
        </Tag>
      ))
    : "—";

const EndpointCard: React.FC<EndpointCardProps> = ({ endpoint, freire }) => (
  <Collapse bordered style={{ marginTop: 8 }}>
    {/* Endpoint metadata */}
    <Panel header={freire(texts.ENDPOINT_LABEL)} key="endpoint">
      <code
        style={{
          display: "block",
          background: "#f5f5f5",
          border: "1px solid #e8e8e8",
          borderRadius: 3,
          padding: "6px 12px",
          fontSize: 13,
          marginBottom: 12,
          wordBreak: "break-all",
        }}
      >
        <Tag color={METHOD_COLORS[endpoint.method]} style={{ fontWeight: 700 }}>
          {endpoint.method}
        </Tag>
        {endpoint.url}
      </code>
      <p style={{ color: "rgba(0,0,0,0.65)", marginBottom: 12 }}>
        {freire(endpoint.description)}
      </p>
      <div style={metaRowStyle}>
        <span>
          <strong>{freire(texts.PROTOCOL_LABEL)}:</strong> {endpoint.protocol}
        </span>
        <span>
          <strong>{freire(texts.CONTENT_TYPE_LABEL)}:</strong> {endpoint.contentType}
        </span>
      </div>
      <p style={{ marginTop: 12, marginBottom: 0, color: "rgba(0,0,0,0.65)" }}>
        <strong style={{ color: "rgba(0,0,0,0.85)" }}>{freire(texts.AUTH_LABEL)}:</strong>{" "}
        <code style={inlineCodeStyle}>{texts.DOC_AUTH_HEADER_LITERAL}</code>{" — "}
        {freire(endpoint.auth)}
      </p>
      {endpoint.accessControl && (
        <p style={{ marginTop: 8, marginBottom: 0, color: "rgba(0,0,0,0.65)" }}>
          <strong style={{ color: "rgba(0,0,0,0.85)" }}>{freire(texts.ACCESS_CONTROL_LABEL)}:</strong>{" "}
          {freire(endpoint.accessControl)}
        </p>
      )}
      {endpoint.notes && (
        <p style={{ marginTop: 8, marginBottom: 0, color: "rgba(0,0,0,0.65)" }}>
          <strong style={{ color: "rgba(0,0,0,0.85)" }}>{freire(texts.NOTES_LABEL)}:</strong>{" "}
          {freire(endpoint.notes)}
        </p>
      )}
    </Panel>

    {/* Parameters */}
    {endpoint.params.length > 0 && (
      <Panel
        header={`${freire(texts.PARAMS_LABEL)} (${endpoint.params.length})`}
        key="params"
      >
        <SimpleTable
          items={endpoint.params}
          columns={[
            { key: "name", title: freire(texts.PARAM_COL_NAME), render: (param) => <code>{param.name}</code> },
            { key: "in", title: freire(texts.PARAM_COL_IN), render: (param) => <Tag color={PARAM_IN_COLORS[param.in]}>{param.in}</Tag> },
            { key: "type", title: freire(texts.PARAM_COL_TYPE), render: (param) => <code>{param.type}</code> },
            { key: "required", title: freire(texts.PARAM_COL_REQUIRED), render: (param) => requiredTag(param.required, freire) },
            { key: "description", title: freire(texts.PARAM_COL_DESCRIPTION), render: (param) => freire(param.description) },
          ]}
        />
      </Panel>
    )}

    {/* Response fields */}
    <Panel
      header={`${freire(texts.RESPONSE_FIELDS_LABEL)} (${endpoint.responseFields.length})`}
      key="fields"
    >
      <SimpleTable
        items={endpoint.responseFields}
        columns={[
          { key: "field", title: freire(texts.FIELD_COL_FIELD), render: (field) => <code>{field.path}</code> },
          { key: "type", title: freire(texts.PARAM_COL_TYPE), render: (field) => <code>{field.type}</code> },
          { key: "description", title: freire(texts.PARAM_COL_DESCRIPTION), render: (field) => freire(field.description) },
          { key: "values", title: freire(texts.FIELD_COL_VALUES), render: (field) => valuesCell(field.values) },
        ]}
      />
    </Panel>

    {/* Response example */}
    <Panel header={freire(texts.RESPONSE_EXAMPLE_LABEL)} key="example">
      <pre style={codeBlockStyle}>{endpoint.responseExample}</pre>
    </Panel>

    {/* Pagination headers */}
    {endpoint.paginationHeaders && endpoint.paginationHeaders.length > 0 && (
      <Panel header={freire(texts.PAGINATION_HEADERS_LABEL)} key="headers">
        <SimpleTable
          items={endpoint.paginationHeaders}
          columns={[
            { key: "name", title: freire(texts.HEADER_COL_NAME), render: (header) => <code>{header.name}</code> },
            { key: "description", title: freire(texts.PARAM_COL_DESCRIPTION), render: (header) => freire(header.description) },
          ]}
        />
      </Panel>
    )}

    {/* Error responses */}
    <Panel
      header={`${freire(texts.ERRORS_LABEL)} (${endpoint.errors.length})`}
      key="errors"
    >
      <SimpleTable
        items={endpoint.errors}
        columns={[
          { key: "code", title: freire(texts.ERROR_COL_CODE), render: (err) => <code>{err.code}</code> },
          { key: "description", title: freire(texts.PARAM_COL_DESCRIPTION), render: (err) => freire(err.description) },
        ]}
      />
    </Panel>

    {/* cURL example */}
    <Panel header={freire(texts.REQUEST_EXAMPLE_LABEL)} key="curl">
      <pre style={codeBlockStyle}>{endpoint.curl}</pre>
    </Panel>
  </Collapse>
);

const OcpiApiPermission: React.FC = () => {
  const { freire } = useContext(FreireContext);
  const [token, setToken] = useState<string | null>(null);
  const [copied, setCopied] = useState<boolean>(false);
  const [btnActionLoading, setBtnActionLoading] = useState<boolean>(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState<boolean>(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | undefined>(undefined);
  // Modal for setting the OCPI party id on a company that doesn't have one yet.
  const [partyTargetCompany, setPartyTargetCompany] = useState<Company | null>(null);
  const [partyModalOpen, setPartyModalOpen] = useState<boolean>(false);
  const [partyIdInput, setPartyIdInput] = useState<string>("");
  const [savingPartyId, setSavingPartyId] = useState<boolean>(false);
  const [partyIdError, setPartyIdError] = useState<string | null>(null);

  const selectedCompany = companies.find((c) => c._id === selectedCompanyId);

  useEffect(() => {
    const fetchCompanies = async () => {
      setLoadingCompanies(true);
      try {
        const { data } = await api.get("/company");
        setCompanies(data);
      } catch (error) {
        console.error(error);
        notification.error({ message: freire(texts.ERROR_FETCHING_COMPANIES) });
      } finally {
        setLoadingCompanies(false);
      }
    };

    fetchCompanies();
  }, [freire]);

  const partyHasToken = (tokens?: OcpiPartyTokens | null) =>
    Boolean(tokens?.first?.token || tokens?.generated?.token || tokens?.access?.token);

  const hasExistingToken = async (partyId: string) => {
    try {
      const { data } = await api.get(`/ocpi/parties/${encodeURIComponent(partyId)}`);
      const party: OcpiPartyDetails | undefined = data?.data;

      return partyHasToken(party?.tokens);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return false;
      }

      throw error;
    }
  };

  const createToken = async (partyId: string) => {
    const { data } = await api.post("/ocpi/parties", {
      party_id: partyId,
    });
    setToken(data.data.credential.token);
  };

  const generateTokenForParty = async (partyId: string) => {
    setBtnActionLoading(true);
    try {
      await createToken(partyId);
    } catch (error) {
      console.error(error);
      notification.error({ message: freire(texts.ERROR_GENERATE_TOKEN) });
    } finally {
      setBtnActionLoading(false);
    }
  };

  const handleGenerateToken = async () => {
    const partyId = selectedCompany?.ocpiPartyId;

    if (!partyId) return;

    setBtnActionLoading(true);
    try {
      const existingToken = await hasExistingToken(partyId);

      if (existingToken) {
        Modal.confirm({
          title: freire(texts.REGENERATE_TOKEN_CONFIRM_TITLE),
          content: freire(texts.REGENERATE_TOKEN_CONFIRM_DESCRIPTION),
          okText: freire(texts.GENERATE_NEW_TOKEN),
          cancelText: freire(texts.CANCEL),
          onOk: () => generateTokenForParty(partyId),
        });
        return;
      }
    } catch (error) {
      console.error(error);
      notification.error({ message: freire(texts.ERROR_CHECK_PARTY_TOKEN) });
      return;
    } finally {
      setBtnActionLoading(false);
    }

    await generateTokenForParty(partyId);
  };

  // A company without an ocpiPartyId can't have a token generated for it, so
  // selecting it opens a modal to set the party id before anything else.
  const handleSelectCompany = (companyId: string) => {
    setToken(null);
    setSelectedCompanyId(companyId);
    const company = companies.find((c) => c._id === companyId);
    if (company && !company.ocpiPartyId) {
      setPartyTargetCompany(company);
      setPartyIdInput("");
      setPartyIdError(null);
      setPartyModalOpen(true);
    }
  };

  const closePartyModal = () => {
    // If the company still has no party id, the selection isn't usable: clear it.
    if (partyTargetCompany && !partyTargetCompany.ocpiPartyId) {
      setSelectedCompanyId(undefined);
    }
    setPartyModalOpen(false);
    setPartyTargetCompany(null);
    setPartyIdError(null);
  };

  const savePartyId = async () => {
    if (!partyTargetCompany) return;
    const value = partyIdInput.trim().toUpperCase();
    if (!/^[A-Z0-9]{3}$/.test(value)) {
      setPartyIdError(freire(texts.PARTY_ID_INVALID_FORMAT));
      return;
    }
    setSavingPartyId(true);
    setPartyIdError(null);
    try {
      await api.put("/company/ocpi-party-id", {
        _id: partyTargetCompany._id,
        ocpiPartyId: value,
      });
      setCompanies((prev) =>
        prev.map((c) =>
          c._id === partyTargetCompany._id ? { ...c, ocpiPartyId: value } : c
        )
      );
      setSelectedCompanyId(partyTargetCompany._id);
      setPartyModalOpen(false);
      setPartyTargetCompany(null);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ?? freire(texts.ERROR_SAVE_PARTY_ID);
      setPartyIdError(message);
    } finally {
      setSavingPartyId(false);
    }
  };

  const copyToken = () => {
    if (!token) return;
    navigator.clipboard.writeText(token).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const eraseToken = () => {
    setToken(null);
  };

  return (
    <div className="container">
      <Breadcrumbs breadcrumbs={[freire(texts.ADMIN), freire(texts.APIS)]} />
      <Card title={freire(texts.OCPI_API_PERMISSIONS_TITLE)}>
        <Form wrapperCol={{ span: 24 }}>
          <Form.Item label={freire(texts.COMPANY)} extra={freire(texts.COMPANY_DESCRIPTION)}>
            <Select
              showSearch
              loading={loadingCompanies}
              value={selectedCompanyId}
              onChange={handleSelectCompany}
              optionFilterProp="children"
            >
              {companies.map((company) => (
                <Option key={company._id} value={company._id}>
                  {company.ocpiPartyId
                    ? `${company.ocpiPartyId} - ${company.companyName}`
                    : company.companyName}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
        <Button
          type="primary"
          icon="check-circle"
          loading={btnActionLoading}
          disabled={btnActionLoading || !selectedCompany?.ocpiPartyId}
          onClick={handleGenerateToken}
          style={{ marginRight: 14 }}
        >
          {freire(texts.GENERATE_NEW_TOKEN)}
        </Button>
        {token && (
          <>
            <Alert
              message={
                <>
                  <b>🔒 {freire(texts.SUCCESS_GENERATE_TOKEN)} </b> <br />{" "}
                  {freire(texts.SUCCESS_GENERATE_TOKEN_MESSAGE)}{" "}
                </>
              }
              type="success"
              showIcon
              style={{ marginTop: 24 }}
            />
            <Input.TextArea
              value={token}
              readOnly
              autoSize
              style={{ marginTop: 24, marginBottom: 14, fontFamily: "monospace" }}
            />
            <Button
              icon={copied ? "check" : "copy"}
              onClick={copyToken}
              type="default"
              style={{ marginRight: 14 }}
            >
              {freire(copied ? texts.TOKEN_COPIED : texts.COPY_TOKEN)}
            </Button>
            <Button type="primary" onClick={eraseToken}>
              {freire(texts.CLOSE_TOKEN)}
            </Button>
          </>
        )}
        <Modal
          title={freire(texts.SET_PARTY_ID_TITLE)}
          visible={partyModalOpen}
          onOk={savePartyId}
          onCancel={closePartyModal}
          confirmLoading={savingPartyId}
          okText={freire(texts.SAVE)}
          cancelText={freire(texts.CANCEL)}
        >
          <p>{freire(texts.SET_PARTY_ID_DESCRIPTION)}</p>
          <p>{freire(texts.SET_PARTY_ID_INSTRUCTIONS)}</p>
          <Input
            value={partyIdInput}
            maxLength={3}
            placeholder="ABC"
            style={{ fontFamily: "monospace", textTransform: "uppercase" }}
            onChange={(e) => {
              setPartyIdInput(
                e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "")
              );
              setPartyIdError(null);
            }}
            onPressEnter={savePartyId}
          />
          {partyIdError && (
            <Alert
              type="error"
              message={partyIdError}
              showIcon
              style={{ marginTop: 12 }}
            />
          )}
        </Modal>
      </Card>
      <br />
      <Card title={freire(texts.OCPI_API_DOCUMENTATION_TITLE)}>
        {freire(texts.OCPI_API_DOCUMENTATION_DESCRIPTION)}
        <br />
        <br />
        <Collapse accordion>
          {API_DOCS.map((endpoint) => (
            <Panel
              key={endpoint.url}
              header={
                <span>
                  <Tag color={METHOD_COLORS[endpoint.method]} style={{ fontWeight: 700 }}>
                    {endpoint.method}
                  </Tag>
                  <strong>{freire(endpoint.name)}</strong>
                </span>
              }
            >
              <EndpointCard endpoint={endpoint} freire={freire} />
            </Panel>
          ))}
        </Collapse>
        <br />
        📖 <b>{freire(texts.COMPLETE_DOCS_AT)}</b>{" "}
        <a
          href="https://evroaming.org/wp-content/uploads/2024/11/OCPI-2.2.1-d2.pdf"
          target="_blank"
          rel="noopener noreferrer"
        >
          {freire(texts.DOCS_LINK_HERE)}
        </a>
        .
      </Card>
    </div>
  );
};

export default OcpiApiPermission;
